import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AdminLayout from "../components/AdminLayout";
import Select from "react-select";
import {
  FaCar,
  FaTruck,
  FaBus,
  FaTaxi,
  FaTruckPickup,
  FaCogs,
  FaShuttleVan,
  FaShippingFast,
  FaThLarge,
  FaTimes,
  FaWindowClose,
  FaArrowLeft,
} from "react-icons/fa";
import {
  Search,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  User,
  Phone,
  MapPin,
  Activity,
  Truck,
  Users,
} from "lucide-react";
import { fetchUsers } from "../actions/UserActions";
import { fetchVehicles as fetchVehiclesList } from "../actions/VehicleActions";

const POLLING_INTERVAL = 300000; // 5 minutes

// Stat Card Component
function StatCard({ title, value, icon: Icon, color }) {
  const colorClasses = {
    blue:   { bg: "bg-blue-50",    iconBg: "bg-blue-100",    text: "text-blue-600"    },
    green:  { bg: "bg-emerald-50", iconBg: "bg-emerald-100", text: "text-emerald-600" },
    yellow: { bg: "bg-yellow-50",  iconBg: "bg-yellow-100",  text: "text-yellow-600"  },
    purple: { bg: "bg-purple-50",  iconBg: "bg-purple-100",  text: "text-purple-600"  },
  };
  const colors = colorClasses[color] || colorClasses.blue;
  return (
    <div className={`${colors.bg} border border-gray-100 rounded-lg p-3 transition-all hover:shadow-sm`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className={`${colors.iconBg} p-1.5 rounded-lg`}>
          <Icon size={14} className={colors.text} />
        </div>
      </div>
      <p className="text-xl font-semibold text-gray-900">{value}</p>
      <p className="text-xs text-gray-600 mt-0.5">{title}</p>
    </div>
  );
}

// Vehicle Type Button Component
const VehicleTypeButton = ({ code, label, icon, counts, active, onClick }) => {
  const colorMap = {
    ALL: "bg-gray-50 border-gray-200 hover:border-gray-300",
    C:   "bg-blue-50 border-blue-200 hover:border-blue-300",
    F:   "bg-yellow-50 border-yellow-200 hover:border-yellow-300",
    V:   "bg-teal-50 border-teal-200 hover:border-teal-300",
    L:   "bg-red-50 border-red-200 hover:border-red-300",
    S:   "bg-purple-50 border-purple-200 hover:border-purple-300",
    CA:  "bg-indigo-50 border-indigo-200 hover:border-indigo-300",
    B:   "bg-emerald-50 border-emerald-200 hover:border-emerald-300",
    CH:  "bg-orange-50 border-orange-200 hover:border-orange-300",
    CR:  "bg-gray-50 border-gray-200 hover:border-gray-300",
  };
  const activeClass = active
    ? "border-2 border-blue-500 bg-blue-50 shadow-sm"
    : colorMap[code] || "bg-gray-50 border-gray-200";

  return (
    <button
      onClick={onClick}
      className={`p-3 rounded-lg border transition-all hover:shadow-sm text-left ${activeClass}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div className="text-base">{icon}</div>
        <span className="text-sm font-semibold text-gray-900">{label}</span>
      </div>
      <div className="text-[10px] text-gray-500 space-y-0.5">
        <div className="flex justify-between">
          <span>Total:</span>
          <span className="font-medium">{counts.total}</span>
        </div>
        <div className="flex justify-between">
          <span>Active:</span>
          <span className="font-medium text-emerald-600">{counts.active}</span>
        </div>
        {/* FIX: show inactive count (from File 2) */}
        <div className="flex justify-between">
          <span>Inactive:</span>
          <span className="font-medium text-red-500">{counts.inactive}</span>
        </div>
        <div className="flex justify-between">
          <span>Assigned:</span>
          <span className="font-medium text-blue-600">{counts.assigned}</span>
        </div>
      </div>
    </button>
  );
};

// Vehicle List Item Component
function VehicleListItem({ vehicle, typeIcons, onSelect }) {
  const isOnline = vehicle.oStatus === "Online";
  const isAssigned = vehicle.driverSno;

  return (
    <div className="p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-all hover:shadow-sm">
      <div className="flex items-center justify-between">
        {/* FIX: row click passes true to open modal (from File 2) */}
        <div
          className="flex items-center gap-3 flex-1 cursor-pointer"
          onClick={() => onSelect(vehicle.vehicleId, true)}
        >
          <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-lg">
            {typeIcons[vehicle.vehicleTypeCode]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">
                {vehicle.vehicleId}
              </span>
              <span className="text-xs text-gray-500">
                {vehicle.vehicleType} • {vehicle.weightCat}
              </span>
            </div>
            {isAssigned ? (
              <div className="flex items-center gap-2 text-xs text-gray-600 mt-0.5">
                <User size={10} />
                <span className="truncate">{vehicle.driverName || vehicle.driverSno}</span>
                <span>•</span>
                <Phone size={10} />
                <span className="truncate">{vehicle.driverMobile || "-"}</span>
              </div>
            ) : (
              <p className="text-xs text-red-500 mt-0.5">Not Assigned</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
          {isAssigned && (
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                isOnline
                  ? "bg-red-50 text-red-700"
                  : "bg-emerald-50 text-emerald-700"
              }`}
            >
              {isOnline ? "Online" : "Offline"}
            </span>
          )}
          {/* FIX: explicit Assign/Reassign button opens modal (from File 2 intent) */}
          <button
            onClick={() => onSelect(vehicle.vehicleId, true)}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-gray-900 hover:bg-gray-700 rounded-lg transition"
          >
            <Users size={11} />
            {isAssigned ? "Reassign" : "Assign"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Driver Select Styles
const selectStyles = {
  control: (base) => ({
    ...base,
    borderColor: "#e5e7eb",
    borderRadius: "0.5rem",
    padding: "0.125rem",
    "&:hover": { borderColor: "#9ca3af" },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#111827" : state.isFocused ? "#f3f4f6" : "white",
    color: state.isSelected ? "white" : "#111827",
    "&:active": { backgroundColor: "#111827" },
  }),
};

export default function AdminVehicleAssign() {
  const dispatch = useDispatch();
  const { authKey } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [vehicleId, setVehicleId]         = useState("");
  const [driverId, setDriverId]           = useState("");
  const [vehicle, setVehicle]             = useState(null);
  const [drivers, setDrivers]             = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [vehicles, setVehicles]           = useState([]);
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState("");
  const [loading, setLoading]             = useState(false);
  const [fetching, setFetching]           = useState(false);
  const [message, setMessage]             = useState("");
  const [sidebarOpen, setSidebarOpen]     = useState(true);
  const [showModal, setShowModal]         = useState(false);

  const typeMap = {
    C:  "Car",
    F:  "Forklift",
    V:  "Van",
    L:  "Lorry",
    S:  "SUV",
    CA: "Cab",
    B:  "Bus",
    CH: "Cherry Picker",
    T:  "Trailer",
    CR: "Crane",
  };

  const typeIcons = {
    C:   <FaCar size={16} />,
    F:   <FaCogs size={16} />,
    V:   <FaShuttleVan size={16} />,
    L:   <FaTruckPickup size={16} />,
    S:   <FaTruck size={16} />,
    CA:  <FaTaxi size={16} />,
    B:   <FaBus size={16} />,
    CH:  <FaShippingFast size={16} />,
    T:   <FaTruck size={16} />,
    CR:  <FaCogs size={16} />,
    ALL: <FaThLarge size={16} />,
  };

  const vehicleTypeCodeMap = {
    Car: "C", Forklift: "F", Van: "V", Lorry: "L", SUV: "S",
    Cab: "CA", Bus: "B", "Cherry Picker": "CH", Trailer: "T", Crane: "CR",
  };

  const statusCodeMap        = { Active: "A", Inactive: "I" };
  const bookingStatusCodeMap = { Booking: "B", Pending: "P", "": "" };
  const onlineStatusCodeMap  = { Online: "O", Offline: "F", "": "F" };

  // ─── Fetch drivers ────────────────────────────────────────────────────────
  const fetchDrivers = async () => {
    try {
      const users = await dispatch(fetchUsers());
      const options =
        users
          ?.filter((d) => d.role === "Driver" && d.status === "Active")
          .map((d) => ({
            value: d.ServiceNo,
            label: `${d.ServiceNo} - ${d.Name}`,
            mobile: d.MobileNo,
          })) || [];
      setDrivers(options);
    } catch (err) {
      console.error(err);
      // FIX: match File 2 emoji prefix so message coloring works consistently
      setMessage("❌ Failed to fetch drivers");
    }
  };

  // ─── Fetch vehicles ───────────────────────────────────────────────────────
  const fetchVehicles = async () => {
    setFetching(true);
    try {
      const rawList = await dispatch(fetchVehiclesList());
      const mapped = rawList.map((v) => {
        const assignedDriver = drivers.find((d) => d.value === v.driverSno);
        return {
          ...v,
          vehicleTypeCode:
            Object.keys(typeMap).find((k) => typeMap[k] === v.vehicleType) ||
            v.vehicleType,
          driverName:   assignedDriver?.label  || "",
          driverMobile: assignedDriver?.mobile || "",
          oStatus: v.oStatus ? v.oStatus : "Offline",
        };
      });
      setVehicles(mapped);
      if (vehicle) {
        const updated = mapped.find((vv) => vv.vehicleId === vehicle.vehicleId);
        if (updated) {
          setVehicle(updated);
          setSelectedDriver(
            drivers.find((d) => d.value === updated.driverSno) || null
          );
        }
      }
    } catch (err) {
      console.error(err);
      setMessage("❌ Failed to fetch vehicles");
    } finally {
      setFetching(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDrivers(); }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (drivers.length) fetchVehicles(); }, [drivers]);

  useEffect(() => {
    const interval = setInterval(fetchVehicles, POLLING_INTERVAL);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drivers, vehicle]);

  // ─── Search by vehicle ID ─────────────────────────────────────────────────
  const searchByVehicleId = () => {
    if (!vehicleId.trim()) {
      setMessage("⚠️ Please enter Vehicle ID");
      return;
    }
    const found = vehicles.find(
      (v) =>
        v.status === "Active" &&
        v.vehicleId?.toUpperCase() === vehicleId.toUpperCase()
    );
    if (!found) {
      setVehicle(null);
      setSelectedDriver(null);
      setShowModal(false);
      setMessage("❌ Active vehicle not found");
    } else {
      handleVehicleClick(found.vehicleId, true);
    }
  };

  // ─── Search by driver ID ──────────────────────────────────────────────────
  const searchByDriverId = () => {
    if (!driverId.trim()) {
      setMessage("⚠️ Please enter Driver ID");
      return;
    }
    const found = vehicles.find(
      (v) => v.status === "Active" && v.driverSno === driverId
    );
    if (!found) {
      setVehicle(null);
      setSelectedDriver(null);
      setShowModal(false);
      setMessage("❌ No vehicle assigned to this driver");
    } else {
      handleVehicleClick(found.vehicleId, true);
    }
  };

  const clearVehicleFilter = () => {
    setVehicleId("");
    setVehicle(null);
    setSelectedDriver(null);
    setMessage("");
    setShowModal(false);
  };

  const clearDriverFilter = () => {
    setDriverId("");
    setVehicle(null);
    setSelectedDriver(null);
    setMessage("");
    setShowModal(false);
  };

  const getAssignedDriverIds = () =>
    vehicles
      .filter((v) => v.driverSno && v.driverSno !== "")
      .map((v) => v.driverSno);

  const getAvailableDrivers = () => {
    if (!vehicle) return [];
    const assignedDrivers = getAssignedDriverIds();
    return drivers.filter((d) => {
      if (vehicle.driverSno === d.value) return true;
      return !assignedDrivers.includes(d.value);
    });
  };

  // ─── Load latest vehicle data then optionally open modal ─────────────────
  const handleVehicleClick = async (vid, openModal = false) => {
    setMessage("");
    try {
      const rawList = await dispatch(fetchVehiclesList());
      const latestVehicle = rawList.find((v) => v.vehicleId === vid);
      if (!latestVehicle) {
        setMessage("❌ Vehicle not found");
        return;
      }
      const assignedDriver =
        drivers.find((d) => d.value === latestVehicle.driverSno) || null;
      const vehicleObj = {
        ...latestVehicle,
        vehicleTypeCode:
          Object.keys(typeMap).find(
            (k) => typeMap[k] === latestVehicle.vehicleType
          ) || latestVehicle.vehicleType,
        driverName:   assignedDriver?.label  || "",
        driverMobile: assignedDriver?.mobile || "",
        oStatus: latestVehicle.oStatus ? latestVehicle.oStatus : "Offline",
      };
      setVehicle(vehicleObj);
      setSelectedDriver(assignedDriver);
      setMessage(
        vehicleObj.oStatus === "Online"
          ? "⚠️ Vehicle is ONLINE. Cannot assign/unassign driver."
          : "✅ Vehicle loaded. You can assign/unassign driver."
      );
      if (openModal) setShowModal(true);
    } catch (err) {
      console.error(err);
      setMessage("❌ Failed to fetch vehicle status");
    }
  };

  // ─── Submit assignment / unassignment ────────────────────────────────────
  const updateDriver = async () => {
    if (!vehicle) return;
    if (vehicle.oStatus === "Online") {
      // FIX: use setMessage (better UX than alert) — File 1 approach kept
      setMessage("❌ Cannot assign or unassign driver. Vehicle is currently ONLINE.");
      return;
    }
    if (vehicle.status !== "Active") {
      setMessage("❌ Cannot assign driver to inactive vehicles");
      return;
    }

    setLoading(true);
    setMessage("");

    const isUnassign = !selectedDriver || !selectedDriver.value;
    const payload = [
      {
        vehicleId:   vehicle.vehicleId,
        vehicleType: vehicleTypeCodeMap[vehicle.vehicleType] || vehicle.vehicleType,
        weightCat:   vehicle.weightCat,
        driverSno:   isUnassign ? "" : selectedDriver.value,
        status:      statusCodeMap[vehicle.status] || vehicle.status,
        bStatus:     isUnassign ? "" : bookingStatusCodeMap[vehicle.bStatus] || vehicle.bStatus || "",
        latitude:    vehicle.latitude  || "0",
        longitude:   vehicle.longitude || "0",
        oStatus:     isUnassign ? "F" : onlineStatusCodeMap[vehicle.oStatus] || "F",
      },
    ];

    try {
      // FIX: production endpoint (from File 2)
      const res = await axios.post(
        "https://aeroviewbackend.dockyardsoftware.com/VehicleRegister/PostVehicleDetails",
        payload,
        { headers: { "Content-Type": "application/json", "auth-key": authKey } }
      );

      if (res.data.statusCode === 200) {
        const successMessage = selectedDriver
          ? "✅ Driver assigned successfully!"
          : "✅ Driver unassigned successfully!";
        setMessage(successMessage);
        await fetchVehicles();
        // FIX: reset all state on success (from File 2)
        setShowModal(false);
        setVehicle(null);
        setSelectedDriver(null);
        setVehicleId("");
        setDriverId("");
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage("❌ Update failed: " + (res.data.result || "Unknown error"));
      }
    } catch (err) {
      console.error(err);
      setMessage("❌ Network or API error");
    } finally {
      setLoading(false);
    }
  };

  // ─── Vehicle type filter ──────────────────────────────────────────────────
  const handleTypeClick = (code) => {
    if (code === "ALL" || vehicleTypeFilter === code) {
      setVehicleTypeFilter("");
    } else {
      setVehicleTypeFilter(code);
    }
    // FIX: always reset vehicle/modal when changing filter (from File 2)
    setVehicle(null);
    setSelectedDriver(null);
    setShowModal(false);
  };

  // FIX: include inactive count in returned object (from File 2)
  const getVehicleCounts = (code) => {
    const list =
      code === "ALL"
        ? vehicles
        : vehicles.filter((v) => v.vehicleTypeCode === code);
    const total    = list.length;
    const active   = list.filter((v) => v.status === "Active").length;
    const inactive = list.filter((v) => v.status !== "Active").length;
    const assigned = list.filter((v) => v.driverSno).length;
    return { total, active, inactive, assigned };
  };

  const filteredVehicles = vehicleTypeFilter
    ? vehicles.filter(
        (v) => v.vehicleTypeCode === vehicleTypeFilter && v.status === "Active"
      )
    : vehicles.filter((v) => v.status === "Active");

  // FIX: also clear vehicle/driver on modal close (from File 2)
  const closeModal = () => {
    setShowModal(false);
    setVehicle(null);
    setSelectedDriver(null);
  };

  // Stats
  const totalVehicles    = vehicles.length;
  const activeVehicles   = vehicles.filter((v) => v.status === "Active").length;
  const assignedVehicles = vehicles.filter((v) => v.driverSno).length;
  const onlineVehicles   = vehicles.filter((v) => v.oStatus === "Online").length;

  // Message style helper — uses ✅/⚠️/❌ prefixes now consistent across all messages
  const msgStyle = () => {
    if (message.startsWith("✅"))
      return "bg-emerald-50 text-emerald-700 border border-emerald-200";
    if (message.startsWith("⚠️"))
      return "bg-blue-50 text-blue-700 border border-blue-200";
    return "bg-red-50 text-red-700 border border-red-200";
  };

  const msgIcon = () => {
    if (message.startsWith("✅")) return <CheckCircle size={14} />;
    if (message.startsWith("⚠️")) return <AlertCircle size={14} />;
    return <XCircle size={14} />;
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Truck size={20} />
              Vehicle Assignment
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Assign or unassign drivers to vehicles in your fleet
            </p>
          </div>
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
          >
            <FaArrowLeft size={12} />
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <StatCard title="Total Vehicles" value={totalVehicles}    icon={Truck}    color="blue"   />
        <StatCard title="Active"         value={activeVehicles}   icon={Activity} color="green"  />
        <StatCard title="Assigned"       value={assignedVehicles} icon={Users}    color="purple" />
        <StatCard title="Online"         value={onlineVehicles}   icon={MapPin}   color="yellow" />
      </div>

      {/* Search */}
      <div className="bg-white border border-gray-100 rounded-lg p-3 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              className="w-full pl-8 pr-20 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
              placeholder="Enter Vehicle ID"
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchByVehicleId()}
            />
            {vehicleId && (
              <button onClick={clearVehicleFilter} className="absolute right-12 top-1/2 -translate-y-1/2">
                <FaTimes size={12} className="text-gray-400 hover:text-gray-600" />
              </button>
            )}
            <button
              onClick={searchByVehicleId}
              className="absolute right-1 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-2.5 py-0.5 text-xs rounded hover:bg-gray-800 transition"
            >
              Search
            </button>
          </div>

          <div className="relative">
            <User size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              className="w-full pl-8 pr-20 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
              placeholder="Enter Driver ID"
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchByDriverId()}
            />
            {driverId && (
              <button onClick={clearDriverFilter} className="absolute right-12 top-1/2 -translate-y-1/2">
                <FaTimes size={12} className="text-gray-400 hover:text-gray-600" />
              </button>
            )}
            <button
              onClick={searchByDriverId}
              className="absolute right-1 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-2.5 py-0.5 text-xs rounded hover:bg-gray-800 transition"
            >
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Vehicle Type Filters */}
      <div className="mb-4">
        <div className="flex items-center gap-1.5 mb-2">
          <FaThLarge size={12} className="text-gray-400" />
          <span className="text-xs text-gray-500">Filter by type</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
          <VehicleTypeButton
            code="ALL"
            label="All"
            icon={typeIcons.ALL}
            counts={getVehicleCounts("ALL")}
            active={vehicleTypeFilter === ""}
            onClick={() => handleTypeClick("ALL")}
          />
          {Object.entries(typeMap).map(([code, name]) => (
            <VehicleTypeButton
              key={code}
              code={code}
              label={name}
              icon={typeIcons[code]}
              counts={getVehicleCounts(code)}
              active={vehicleTypeFilter === code}
              onClick={() => handleTypeClick(code)}
            />
          ))}
        </div>
      </div>

      {/* Alert banner */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2.5 mb-4">
        <div className="flex items-center gap-2">
          <AlertCircle size={14} className="text-yellow-600" />
          <p className="text-xs text-yellow-700">
            Please refresh before assigning drivers and verify the vehicle's online status.
          </p>
        </div>
      </div>

      {/* Status message */}
      {message && (
        <div className={`mb-4 p-2.5 rounded-lg ${msgStyle()}`}>
          <div className="flex items-center gap-2">
            {msgIcon()}
            <p className="text-xs">{message}</p>
          </div>
        </div>
      )}

      {/* Vehicle list */}
      <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden mb-4">
        <div className="p-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Truck size={14} className="text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-900">Active Vehicles</h2>
            </div>
            <button
              onClick={fetchVehicles}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition"
              disabled={fetching}
            >
              <RefreshCw size={12} className={fetching ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        <div className="max-h-80 overflow-y-auto p-3 space-y-2">
          {fetching ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw size={16} className="text-gray-400 animate-spin" />
              <span className="ml-2 text-xs text-gray-500">Loading...</span>
            </div>
          ) : filteredVehicles.length > 0 ? (
            // FIX: limit to 10 items for performance (from File 2)
            filteredVehicles.slice(0, 10).map((v) => (
              <VehicleListItem
                key={v.vehicleId}
                vehicle={v}
                typeIcons={typeIcons}
                onSelect={handleVehicleClick}
              />
            ))
          ) : (
            <div className="text-center py-8">
              <div className="flex flex-col items-center gap-1.5">
                <Truck size={24} className="text-gray-300" />
                <p className="text-xs text-gray-500">No active vehicles found</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Assignment Modal */}
      {showModal && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            onClick={closeModal}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full border border-gray-100 max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-gray-900 rounded-lg">
                    <Truck size={16} className="text-white" />
                  </div>
                  <h2 className="text-base font-semibold text-gray-900">
                    Vehicle Assignment
                  </h2>
                </div>
                <button
                  onClick={closeModal}
                  className="p-1 hover:bg-gray-100 rounded-lg transition"
                >
                  <FaWindowClose size={16} className="text-gray-400" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-4">
                {/* Vehicle Info */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                      {typeIcons[vehicle?.vehicleTypeCode || "ALL"]}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {vehicle?.vehicleId}
                      </p>
                      <p className="text-xs text-gray-500">
                        {vehicle?.vehicleType} • {vehicle?.weightCat}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Vehicle Details Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="text-xs text-gray-500">Type</label>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">
                      {vehicle?.vehicleType || "-"}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Weight</label>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">
                      {vehicle?.weightCat || "-"}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Status</label>
                    <p className={`text-sm font-medium mt-0.5 ${
                      vehicle?.status === "Active" ? "text-emerald-600" : "text-gray-600"
                    }`}>
                      {vehicle?.status || "-"}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Online Status</label>
                    <p className={`text-sm font-medium mt-0.5 ${
                      vehicle?.oStatus === "Online" ? "text-red-600" : "text-emerald-600"
                    }`}>
                      {vehicle?.oStatus || "Offline"}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Current Driver</label>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">
                      {vehicle?.driverName || "Not Assigned"}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Driver Mobile</label>
                    <p className="text-sm text-gray-900 mt-0.5">
                      {vehicle?.driverMobile || "-"}
                    </p>
                  </div>
                </div>

                {/* Driver Selection */}
                <div className="mb-4">
                  <label className="text-xs font-medium text-gray-700 mb-1.5 block">
                    Assign / Unassign Driver
                  </label>
                  <Select
                    styles={selectStyles}
                    options={[
                      { value: "", label: "-- Unassign Driver --" },
                      ...getAvailableDrivers(),
                    ]}
                    value={
                      selectedDriver || { value: "", label: "-- Unassign Driver --" }
                    }
                    onChange={(option) => {
                      if (!option || !option.value) {
                        setSelectedDriver(null);
                        return;
                      }
                      setSelectedDriver(option);
                    }}
                    isDisabled={vehicle?.oStatus === "Online"}
                    placeholder="Select a driver..."
                  />
                  {vehicle?.oStatus === "Online" && (
                    <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                      <AlertCircle size={10} />
                      Cannot assign or unassign while vehicle is ONLINE.
                    </p>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex gap-2 p-4 border-t border-gray-100 sticky bottom-0 bg-white">
                <button
                  onClick={closeModal}
                  className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  onClick={updateDriver}
                  disabled={loading || vehicle?.oStatus === "Online"}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Driver Assignment"
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}